import Add from "@mui/icons-material/Add";
import { Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem, GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-premium";
import { TextCellWithTooltip } from "components/DataGridCell";
import { StatusChip } from "components/StatusChip";
import { ReceivedEmailOutcome, ReceivedEmailRejectionReason } from "generated-graphql/graphql";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";

import type { InboundEmailRow } from "./senderDialogValues";

import { attachmentsLabel, outcomeDisplay, receivedLabel, REJECTION_LABELS } from "./displayText";
import { InboundExpandCell } from "./InboundExpandCell";

/**
 * The rejection reason is folded into the Status chip rather than given a column of
 * its own, so there is no column for the filter popover to derive a chip from. It
 * matters enough to filter by — "show me everything we refused as not allowlisted" is
 * the question this listing exists to answer — so it is declared directly.
 */
export const INBOUND_ADDITIONAL_FILTER_KEYS: FilterKey[] = [
  {
    // Whether resolution could name an organization at all. Declared separately
    // rather than as a null option on the Organization column's tenant picker:
    // that picker is shared by 25 grids and serializes tenant ids, with no way to
    // say "none".
    key: "attributed",
    header: "Attributed",
    filterKeyType: "boolean",
  },
  {
    key: "rejectionReason",
    header: "Rejection Reason",
    filterKeyType: "enum",
    enumValues: Object.values(ReceivedEmailRejectionReason),
    // The popover hands us a plain string, so read the label map by key rather than
    // routing back through outcomeDisplay, which wants a whole row.
    enumPresentationFunction: (value) =>
      Object.entries(REJECTION_LABELS).find(([reason]) => reason === value)?.[1] ?? value,
  },
];

/**
 * How each outcome reads in the filter popover. The chip on a row says what happened
 * to *that* email ("Partial · 2 failed"); these name the set being selected.
 */
export const OUTCOME_FILTER_LABELS: Record<string, string> = {
  [ReceivedEmailOutcome.NeedsAttention]: "Needs attention",
  [ReceivedEmailOutcome.Filed]: "Filed",
  [ReceivedEmailOutcome.Partial]: "Partially filed",
  [ReceivedEmailOutcome.NothingFiled]: "Nothing filed",
  [ReceivedEmailOutcome.NoAttachments]: "No attachments",
  [ReceivedEmailOutcome.Refused]: "Refused",
  [ReceivedEmailOutcome.Errored]: "Errored",
  [ReceivedEmailOutcome.Processing]: "Processing",
};

/**
 * How far to indent the detail panel so its content starts where the row's own data
 * does, rather than flush against the grid's left edge.
 *
 * Measured rather than derived: 50px for the chevron column plus the 10px of cell
 * padding before a cell's text. `GRID_DETAIL_PANEL_TOGGLE_COL_DEF.width` says 40,
 * but the shared DataGrid renders that column at 50, so reading the constant lands
 * the panel 10px short of the Received column — which is exactly the misalignment
 * this is here to fix.
 */
export const DETAIL_PANEL_INDENT_PX = 60;

/** None carries a value worth filtering on: derived text, a button, a chevron. */
export const INBOUND_EXCLUDE_FILTER_COLUMNS = [
  "attachments",
  "addSender",
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF.field,
  // The popover derives a filter from every column it is not told to skip, and these
  // two produced controls the resolver does not parse: setting them changed nothing
  // and the grid still returned every row. The search box already matches both
  // fields, so this removes a second, broken path to the same thing.
  "fromAddress",
  "subject",
];

/**
 * The per-column menu offers sort, filter, hide-column and manage-columns — all four
 * of which the toolbar above now owns, and headers already sort on click. Leaving it
 * on also cost a phantom scrollbar: with flex columns filling the width exactly, the
 * last header's menu button overhangs by its own 23px and the grid scrolls to show
 * nothing.
 */
const noMenu = (
  col: OmnisearchGridColDef<InboundEmailRow>
): OmnisearchGridColDef<InboundEmailRow> => ({ ...col, disableColumnMenu: true });

export function buildInboundColumns({
  onAddSender,
}: {
  onAddSender?: (fromAddress: string) => void;
} = {}): OmnisearchGridColDef<InboundEmailRow>[] {
  const base: OmnisearchGridColDef<InboundEmailRow>[] = [
    // The column spread carries the sizing and alignment; the cell is ours because
    // the default renderCell draws nothing through this wrapper — it reads grid state
    // the shared DataGrid does not thread to it. Driving `toggleDetailPanel` off
    // `apiRef` is what the other detail-panel grids here do for the same reason.
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      // Not the reader's to hide, and it has no header to name it by in the
      // Manage Columns list.
      hideable: false,
      renderCell: ({ id }) => <InboundExpandCell id={id} />,
    },
    {
      field: "receivedAt",
      headerName: "Received",
      flex: 1,
      minWidth: 130,
      // The app's standard range filter, rather than a bespoke window control beside
      // the heading — which sat outside the filter popover where every other filter
      // lives, and read as though it governed the whole page rather than this grid.
      filterKeyType: "date",
      valueGetter: (_v, row) => new Date(row.receivedAt),
      renderCell: ({ row }) => (
        <Typography variant="body2">{receivedLabel(row.receivedAt)}</Typography>
      ),
    },
    {
      field: "tenant",
      headerName: "Organization",
      flex: 1.2,
      minWidth: 140,
      // Serialized by MultipleTenantPicker as `tenantId:a|b`, which the resolver's
      // `tenantId` chip resolves. The picker cannot express "no organization" — the
      // separate `attributed` filter above is how that is asked for.
      filterKeyType: "multipleTenant",
      valueGetter: (_v, row) => row.tenant?.name ?? "",
      // Left empty when nothing matched, rather than labelled. An empty cell in a
      // column of names is the most obvious thing on the row, and the Outcome column
      // already says why — a word here only invited the question of what it meant.
      renderCell: ({ row }) =>
        row.tenant ? <TextCellWithTooltip>{row.tenant.name}</TextCellWithTooltip> : null,
    },
    {
      field: "fromAddress",
      headerName: "From",
      flex: 2,
      minWidth: 190,
      valueGetter: (_v, row) => row.fromAddress ?? "",
      // Single line on purpose. This used to carry "Not on any allowlist" underneath,
      // which the Outcome column already says — and a cell that is two lines on some
      // rows and one on others makes the grid change height as you filter, which is
      // what made the page jump around.
      renderCell: ({ row }) => <TextCellWithTooltip>{row.fromAddress ?? "—"}</TextCellWithTooltip>,
    },
    {
      field: "subject",
      headerName: "Subject",
      flex: 2,
      minWidth: 160,
      valueGetter: (_v, row) => row.subject || "(no subject)",
      // Clips mid-word without this: the shared grid makes cells flex containers, and
      // `text-overflow` does not apply to a flex item.
      renderCell: ({ row }) => (
        <TextCellWithTooltip>{row.subject || "(no subject)"}</TextCellWithTooltip>
      ),
    },
    {
      field: "attachments",
      headerName: "Attachments",
      flex: 1,
      minWidth: 120,
      sortable: false,
      valueGetter: (_v, row) => attachmentsLabel(row),
    },
    {
      field: "outcome",
      headerName: "Outcome",
      flex: 1.5,
      minWidth: 170,
      filterKeyType: "enum",
      enumValues: Object.values(ReceivedEmailOutcome),
      enumPresentationFunction: (value) => OUTCOME_FILTER_LABELS[value] ?? value,
      renderCell: ({ row }) => {
        const { color, label, help } = outcomeDisplay(row);
        // Always a chip. The old listing drew neutral outcomes as plain text because
        // every row in it was a routine refusal, so the colour said nothing. In one
        // merged grid the outcomes are mixed and the colour is the fastest thing to
        // scan. maxWidth so a long label ellipsizes inside the cell rather than being
        // clipped by it, which is how the old sentence-long labels failed.
        const rendered = <StatusChip color={color} label={label} sx={{ maxWidth: "100%" }} />;
        // StatusChip is not forwardRef, so Tooltip needs an element to attach to. The
        // help text is the whole explanation of what happened, so it always stays.
        return help ? (
          <Tooltip title={help}>
            <span>{rendered}</span>
          </Tooltip>
        ) : (
          rendered
        );
      },
    },
  ];

  if (!onAddSender) return base.map(noMenu);

  const withAction: OmnisearchGridColDef<InboundEmailRow>[] = [
    ...base,
    {
      field: "addSender",
      // `type: "actions"` earns the shared grid's hover reveal. Rendered on every row
      // it was a column of identical plus signs down the right edge, which reads as
      // decoration rather than as something to press.
      type: "actions",
      headerName: "",
      width: 56,
      hideable: false,
      // Only where allowlisting is the fix, which is narrower than "nobody claimed
      // it". An ambiguous sender is already claimed by two organizations, so adding a
      // third makes the mail *more* unroutable — that one is fixed by removing a
      // claim on the senders grid. A switched-off sender is fixed by turning its
      // existing entry back on, and an unreadable From header has no address to add.
      getActions: ({ row }) =>
        row.fromAddress && row.rejectionReason === ReceivedEmailRejectionReason.SenderNotAllowed
          ? [
              <GridActionsCellItem
                key="add"
                icon={
                  <Tooltip title={`Allowlist ${row.fromAddress} for an organization`}>
                    <Add fontSize="small" />
                  </Tooltip>
                }
                // Named per row, so the column is not a wall of identical controls to
                // a screen reader or a test.
                label={`Add ${row.fromAddress} as sender`}
                onClick={() => onAddSender(row.fromAddress ?? "")}
              />,
            ]
          : [],
    },
  ];
  return withAction.map(noMenu);
}
